import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { firebase } from "common/src/config/configureFirebase";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function UserSubscription() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const auth = useSelector((state) => state.auth);
    const currentLanguage = i18n.language || "en";

    const [loading, setLoading] = useState(true);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [error, setError] = useState("");
    const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [receiptUrl, setReceiptUrl] = useState("");
    const [subscribing, setSubscribing] = useState(false);

    // Get server URL dynamically from Firebase config with memoization
    const serverUrl = useMemo(() => {
        // Get project ID dynamically from Firebase config
        let projectId =
            firebase?.config?.projectId || firebase?.app?.options?.projectId;

        // Fallback: try to get from environment variables or other sources
        if (!projectId) {
            // Check if running in web environment and try to get from window object
            if (typeof window !== "undefined" && window.firebaseConfig) {
                projectId = window.firebaseConfig.projectId;
            }
        }

        if (!projectId) {
            console.error(
                "Firebase project ID not found. Make sure Firebase is properly initialized."
            );
            // Fallback to default project ID with warning
            console.warn(
                "Using fallback project ID. Please ensure Firebase is properly configured."
            );
            projectId = "quaumovil"; // fallback
        }

        return `https://us-central1-${projectId}.cloudfunctions.net`;
    }, []); // Empty dependency array since firebase is a stable reference

    useEffect(() => {
        if (auth.profile?.uid) {
            loadSubscriptionData();
        }
    }, [auth.profile?.uid]);

    const loadSubscriptionData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadSubscriptionPlans(),
                loadCurrentSubscription(),
            ]);
        } catch (error) {
            console.error("Error loading subscription data:", error);
            setError(t("error_loading_subscription_data"));
        } finally {
            setLoading(false);
        }
    };

    const loadSubscriptionPlans = async () => {
        try {
            const userType =
                auth.profile?.usertype === "driver" ? "driver" : "customer";
            const response = await fetch(
                `${serverUrl}/subscription-getSubscriptionPlans?userType=${userType}&language=${currentLanguage}`
            );
            const data = await response.json();

            if (data.success && data.plans) {
                const plansArray = Object.keys(data.plans).map((key) => ({
                    ...data.plans[key],
                    key: key,
                }));

                // Filter only active plans
                const activePlans = plansArray.filter(
                    (plan) => plan.isActive === true
                );
                setSubscriptionPlans(activePlans);
            } else {
                setSubscriptionPlans([]);
            }
        } catch (error) {
            console.error("Error loading plans:", error);
            setSubscriptionPlans([]);
        }
    };

    const loadCurrentSubscription = async () => {
        if (!auth.profile?.uid) return;

        try {
            const response = await fetch(
                `${serverUrl}/subscription-getUserSubscription?userId=${auth.profile.uid}`
            );
            const data = await response.json();

            if (data.success && data.subscription) {
                setCurrentSubscription(data.subscription);
            } else {
                setCurrentSubscription(null);
            }
        } catch (error) {
            console.error("Error loading current subscription:", error);
            setCurrentSubscription(null);
        }
    };

    const handleSubscribe = (plan) => {
        setSelectedPlan(plan);
        setSubscribeDialogOpen(true);
    };

    const handleSubscribeConfirm = async () => {
        if (!selectedPlan || !paymentMethod) {
            setError(t("please_select_payment_method"));
            return;
        }

        setSubscribing(true);
        try {
            const response = await fetch(
                `${serverUrl}/subscription-createSubscription`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userId: auth.profile.uid,
                        planId: selectedPlan.key,
                        paymentMethod: paymentMethod,
                        receiptUrl: receiptUrl || "",
                        usedWalletMoney: 0,
                        payableAmount: selectedPlan.price,
                        isPlanChange: false,
                    }),
                }
            );

            const data = await response.json();

            if (data.success) {
                setSubscribeDialogOpen(false);
                setSelectedPlan(null);
                setPaymentMethod("");
                setReceiptUrl("");
                await loadSubscriptionData();
                setError("");
            } else {
                setError(data.error || t("subscription_failed"));
            }
        } catch (error) {
            console.error("Error creating subscription:", error);
            setError(t("subscription_failed"));
        } finally {
            setSubscribing(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "active":
                return "success";
            case "pending":
            case "payment_pending":
                return "warning";
            case "cancelled":
            case "expired":
            case "suspended":
                return "error";
            default:
                return "default";
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "-";
        return moment(timestamp).format("DD/MM/YYYY HH:mm");
    };

    const formatPrice = (price) => {
        return `$${price}`;
    };

    const getPeriodText = (period) => {
        switch (period) {
            case "daily":
                return t("daily");
            case "weekly":
                return t("weekly");
            case "monthly":
                return t("monthly");
            case "yearly":
                return t("yearly");
            default:
                return period;
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                {t("subscriptions")}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Current Subscription */}
            {currentSubscription && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t("current_subscription")}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body1">
                                    <strong>{t("plan")}:</strong>{" "}
                                    {currentSubscription.planName}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t("price")}:</strong>{" "}
                                    {formatPrice(currentSubscription.price)}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t("period")}:</strong>{" "}
                                    {getPeriodText(currentSubscription.period)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body1">
                                    <strong>{t("start_date")}:</strong>{" "}
                                    {formatDate(currentSubscription.startDate)}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t("end_date")}:</strong>{" "}
                                    {formatDate(currentSubscription.endDate)}
                                </Typography>
                                <Chip
                                    label={t(currentSubscription.status)}
                                    color={getStatusColor(
                                        currentSubscription.status
                                    )}
                                    sx={{ mt: 1 }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Available Plans */}
            <Typography variant="h6" gutterBottom>
                {t("available_plans")}
            </Typography>

            {subscriptionPlans.length === 0 ? (
                <Alert severity="info">
                    {t("no_subscription_plans_available")}
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {subscriptionPlans.map((plan) => (
                        <Grid item xs={12} md={4} key={plan.key}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    position: "relative",
                                }}
                            >
                                {plan.isPopular && (
                                    <Chip
                                        label={t("popular")}
                                        color="primary"
                                        sx={{
                                            position: "absolute",
                                            top: 16,
                                            right: 16,
                                            zIndex: 1,
                                        }}
                                    />
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h5" gutterBottom>
                                        {plan.translations?.[currentLanguage]
                                            ?.name || plan.name}
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        color="primary"
                                        gutterBottom
                                    >
                                        {formatPrice(plan.price)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        {getPeriodText(plan.period)}
                                    </Typography>

                                    {plan.description && (
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 2 }}
                                        >
                                            {plan.translations?.[
                                                currentLanguage
                                            ]?.description || plan.description}
                                        </Typography>
                                    )}

                                    {plan.features &&
                                        plan.features.length > 0 && (
                                            <List dense>
                                                {plan.features.map(
                                                    (feature, index) => (
                                                        <ListItem
                                                            key={index}
                                                            sx={{ py: 0 }}
                                                        >
                                                            <ListItemIcon
                                                                sx={{
                                                                    minWidth: 32,
                                                                }}
                                                            >
                                                                <CheckCircleIcon
                                                                    color="success"
                                                                    fontSize="small"
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    feature
                                                                }
                                                                primaryTypographyProps={{
                                                                    variant:
                                                                        "body2",
                                                                }}
                                                            />
                                                        </ListItem>
                                                    )
                                                )}
                                            </List>
                                        )}

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={
                                            currentSubscription?.status ===
                                            "active"
                                        }
                                        sx={{ mt: 2 }}
                                    >
                                        {currentSubscription?.status ===
                                        "active"
                                            ? t("already_subscribed")
                                            : t("subscribe_now")}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Subscribe Dialog */}
            <Dialog
                open={subscribeDialogOpen}
                onClose={() => setSubscribeDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t("subscribe_to_plan")}:{" "}
                    {selectedPlan?.translations?.[currentLanguage]?.name ||
                        selectedPlan?.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {t("plan_details")}
                        </Typography>
                        <Typography variant="body1">
                            <strong>{t("price")}:</strong>{" "}
                            {formatPrice(selectedPlan?.price)}
                        </Typography>
                        <Typography variant="body1">
                            <strong>{t("period")}:</strong>{" "}
                            {getPeriodText(selectedPlan?.period)}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t("payment_method")}</InputLabel>
                        <Select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            label={t("payment_method")}
                        >
                            <MenuItem value="cash">
                                {t("cash_payment")}
                            </MenuItem>
                            <MenuItem value="bank_transfer">
                                {t("bank_transfer")}
                            </MenuItem>
                            <MenuItem value="yappi">
                                {t("yappi_payment")}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label={t("receipt_url")}
                        value={receiptUrl}
                        onChange={(e) => setReceiptUrl(e.target.value)}
                        helperText={t("optional_receipt_url")}
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubscribeDialogOpen(false)}>
                        {t("cancel")}
                    </Button>
                    <Button
                        onClick={handleSubscribeConfirm}
                        variant="contained"
                        disabled={!paymentMethod || subscribing}
                    >
                        {subscribing
                            ? t("subscribing")
                            : t("confirm_subscription")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
